import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { v: "", label: "Todos" },
  { v: "aguardando_aprovacao", label: "Aguardando aprovação" },
  { v: "gratuito", label: "Gratuito" },
  { v: "ativo", label: "Ativo" },
  { v: "pendente", label: "Pendente" },
  { v: "suspenso", label: "Suspenso" },
  { v: "cancelado", label: "Cancelado" },
];

const STATUS_COLOR = {
  gratuito: "bg-[#00A859] text-white",
  ativo: "bg-[#142766] text-white",
  pendente: "bg-yellow-500 text-white",
  suspenso: "bg-[#E60000] text-white",
  cancelado: "bg-zinc-500 text-white",
  aguardando_aprovacao: "bg-zinc-300 text-zinc-700",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR");
}

function StatusBadge({ s }) {
  const c = STATUS_COLOR[s] || "bg-zinc-200";
  return <span className={`px-2 py-0.5 text-xs ${c}`} data-testid={`status-${s}`}>{s}</span>;
}

export default function Assinaturas() {
  const [resumo, setResumo] = useState(null);

  const loadResumo = useCallback(() => {
    api.get("/admin/assinaturas/resumo").then(r => setResumo(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadResumo(); }, [loadResumo]);

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="label-cap">SaaS</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Assinaturas</h1>
        <p className="text-xs text-muted-foreground mt-2">
          Controle de gratuidade, cobrança e status dos parceiros da plataforma.
        </p>
      </div>

      {/* Resumo */}
      {resumo && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border border border-border mb-8">
          <KPI label="Aguardando" v={resumo.montadores.aguardando_aprovacao + resumo.montadoras.aguardando_aprovacao} />
          <KPI label="Gratuitos" v={resumo.montadores.gratuito + resumo.montadoras.gratuito} highlight />
          <KPI label="Ativos" v={resumo.montadores.ativo + resumo.montadoras.ativa} />
          <KPI label="Pendentes" v={resumo.montadores.pendente + resumo.montadoras.pendente} alert={(resumo.montadores.pendente + resumo.montadoras.pendente) > 0} />
          <KPI label="Suspensos" v={resumo.montadores.suspenso + resumo.montadoras.suspensa} alert={(resumo.montadores.suspenso + resumo.montadoras.suspensa) > 0} />
          <KPI label="Cancelados" v={resumo.montadores.cancelado + resumo.montadoras.cancelada} />
        </div>
      )}

      <Tabs defaultValue="montadores">
        <TabsList className="rounded-none">
          <TabsTrigger value="montadores" data-testid="tab-ass-montadores">Montadores</TabsTrigger>
          <TabsTrigger value="montadoras" data-testid="tab-ass-montadoras">Montadoras</TabsTrigger>
          <TabsTrigger value="empresas" data-testid="tab-ass-empresas">Empresas</TabsTrigger>
        </TabsList>

        <TabsContent value="montadores" className="mt-4">
          <ListaMontadores onChange={loadResumo} />
        </TabsContent>
        <TabsContent value="montadoras" className="mt-4">
          <ListaMontadoras onChange={loadResumo} />
        </TabsContent>
        <TabsContent value="empresas" className="mt-4">
          <ListaEmpresas />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ label, v, alert, highlight }) {
  return (
    <div className={`bg-white p-4 ${alert ? "border-l-4 border-l-[#E60000]" : ""} ${highlight ? "border-l-4 border-l-[#00A859]" : ""}`}>
      <div className="label-cap">{label}</div>
      <div className={`font-heading font-extrabold text-2xl mt-1 ${alert ? "text-[#E60000]" : ""}`}>{v}</div>
    </div>
  );
}

function FilterBar({ value, onChange }) {
  return (
    <div className="flex gap-2 flex-wrap mb-4">
      {STATUS_FILTERS.map(f => (
        <button key={f.v}
          onClick={() => onChange(f.v)}
          className={`btn-sharp px-3 py-1.5 text-xs border ${value === f.v ? "brand-bg text-white border-transparent" : "border-border bg-white hover:bg-secondary"}`}
          data-testid={`filter-${f.v || "todos"}`}>
          {f.label}
        </button>
      ))}
    </div>
  );
}

function ActionBtns({ tipo, id, statusAss, onChange, contratoAssinado }) {
  const [busy, setBusy] = useState(false);
  const [contrato, setContrato] = useState(null);

  const doAction = async (action) => {
    setBusy(true);
    try {
      const url = `/admin/assinaturas/${tipo}/${id}/${action}`;
      if (action === "suspender") {
        const motivo = window.prompt("Motivo da suspensão:");
        if (!motivo) { setBusy(false); return; }
        const fd = new FormData();
        fd.append("motivo", motivo);
        await api.post(url, fd);
      } else {
        await api.post(url);
      }
      toast.success(`Ação '${action}' executada`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verContrato = async () => {
    try {
      const r = await api.get(`/admin/contratos/${tipo}/${id}`);
      setContrato(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const isAguardando = statusAss === "aguardando_aprovacao" || !statusAss;
  const isAtivoOuGratuito = statusAss === "gratuito" || statusAss === "ativo";
  const isSuspenso = statusAss === "suspenso";

  return (
    <>
      <div className="flex gap-1 flex-wrap">
        {isAguardando && tipo !== "empresa" && (
          <Button size="sm" onClick={() => doAction("aprovar")} disabled={busy}
            className="btn-sharp brand-bg text-white text-xs h-7" data-testid={`btn-aprovar-${id}`}>
            Aprovar
          </Button>
        )}
        {isAtivoOuGratuito && (
          <Button size="sm" variant="outline" onClick={() => doAction("suspender")} disabled={busy}
            className="btn-sharp text-xs h-7" data-testid={`btn-suspender-${id}`}>
            Suspender
          </Button>
        )}
        {isSuspenso && (
          <Button size="sm" onClick={() => doAction("reativar")} disabled={busy}
            className="btn-sharp bg-[#00A859] hover:bg-[#007a40] text-white text-xs h-7" data-testid={`btn-reativar-${id}`}>
            Reativar
          </Button>
        )}
        {contratoAssinado && tipo !== "empresa" && (
          <Button size="sm" variant="ghost" onClick={verContrato}
            className="btn-sharp text-xs h-7" data-testid={`btn-ver-contrato-${id}`}>
            Contrato
          </Button>
        )}
      </div>
      {contrato && <ContratoModal contrato={contrato} onClose={() => setContrato(null)} />}
    </>
  );
}

function ContratoModal({ contrato, onClose }) {
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";
  const aceites = contrato.aceites_v2 || {};
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="modal-contrato">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border p-5 flex justify-between items-center">
          <div>
            <div className="label-cap">Contrato assinado</div>
            <h2 className="font-heading font-extrabold text-xl">{contrato.tipo || "Contrato Montador"}</h2>
          </div>
          <button onClick={onClose} className="text-2xl" data-testid="modal-contrato-fechar">×</button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <KV label="Versão" v={contrato.versao || "v2.0"} />
          <KV label="Assinado em" v={fmt(contrato.timestamp)} />
          <KV label="IP" v={contrato.ip} />
          <KV label="Dispositivo" v={contrato.dispositivo} mono />
          <KV label="User-Agent" v={contrato.user_agent} mono />

          <div>
            <div className="label-cap mb-2">Declarações aceitas</div>
            {Object.keys(aceites).length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                Contrato gravado em versão anterior (pré-v2). Aceites originais:
                <ul className="mt-1 ml-2 space-y-0.5">
                  {["aceito_regras_operacionais","aceito_regras_garantia","aceito_retorno_30_dias","aceito_politica_bloqueio","aceito_auditoria"].map(k => (
                    <li key={k}>{contrato[k] ? "✓" : "✗"} {k.replace(/_/g, " ")}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <ul className="text-xs space-y-1">
                {Object.entries(aceites).map(([k, v]) => (
                  <li key={k}>{v ? "✓" : "✗"} {k.replace(/_/g, " ")}</li>
                ))}
              </ul>
            )}
          </div>

          {contrato.assinatura && (
            <div>
              <div className="label-cap mb-2">Assinatura digital</div>
              <img src={contrato.assinatura} alt="Assinatura" className="border border-border bg-white max-w-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, v, mono }) {
  return (
    <div>
      <div className="label-cap text-[10px]">{label}</div>
      <div className={`text-sm ${mono ? "font-mono break-all" : ""}`}>{v || "—"}</div>
    </div>
  );
}

function ListaMontadores({ onChange }) {
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = `/admin/assinaturas/montadores${filter ? `?status=${filter}` : ""}`;
    api.get(url).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  useEffect(load, [load]);

  const refresh = () => { load(); onChange?.(); };

  return (
    <>
      <FilterBar value={filter} onChange={setFilter} />
      {loading ? <div className="text-sm text-muted-foreground p-4">Carregando…</div> : (
        <div className="border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <Th>Nome</Th><Th>Cidade</Th><Th>CNPJ</Th>
                <Th>Cadastro</Th><Th>Fim gratuidade</Th>
                <Th>Status</Th><Th>Próx. cobrança</Th><Th>Mensal.</Th><Th>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Nenhum montador no filtro selecionado.</td></tr>
              ) : items.map(m => (
                <tr key={m.id} className="border-t border-border hover:bg-secondary/30" data-testid={`row-mont-${m.id}`}>
                  <Td><strong>{m.nome}</strong><div className="text-xs text-muted-foreground">{m.email}</div></Td>
                  <Td>{m.cidade}/{m.estado}</Td>
                  <Td className="text-xs">{m.cnpj_ativo ? `${m.cnpj} (ativo)` : (m.cnpj ? `${m.cnpj} (inativo)` : "Sem CNPJ")}</Td>
                  <Td>{fmtDate(m.created_at)}</Td>
                  <Td>{fmtDate(m.data_fim_gratuidade)}</Td>
                  <Td><StatusBadge s={m.status_assinatura} /></Td>
                  <Td>{fmtDate(m.proxima_cobranca)}</Td>
                  <Td>R$ {m.valor_mensalidade?.toFixed(2)}</Td>
                  <Td><ActionBtns tipo="montador" id={m.id} statusAss={m.status_assinatura} contratoAssinado={m.contrato_assinado} onChange={refresh} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ListaMontadoras({ onChange }) {
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = `/admin/assinaturas/montadoras${filter ? `?status=${filter}` : ""}`;
    api.get(url).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  useEffect(load, [load]);

  const refresh = () => { load(); onChange?.(); };

  return (
    <>
      <FilterBar value={filter} onChange={setFilter} />
      {loading ? <div className="text-sm text-muted-foreground p-4">Carregando…</div> : (
        <div className="border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <Th>Nome</Th><Th>CNPJ</Th>
                <Th>Cadastro</Th><Th>Fim gratuidade</Th>
                <Th>Status</Th><Th>Atend. mês</Th>
                <Th>Faixa atual</Th><Th>Valor projet.</Th><Th>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Nenhuma montadora no filtro selecionado.</td></tr>
              ) : items.map(m => (
                <tr key={m.id} className="border-t border-border hover:bg-secondary/30" data-testid={`row-monta-${m.id}`}>
                  <Td><strong>{m.nome}</strong><div className="text-xs text-muted-foreground">{m.email || "—"}</div></Td>
                  <Td className="text-xs font-mono">{m.cpf_cnpj || "—"}</Td>
                  <Td>{fmtDate(m.created_at)}</Td>
                  <Td>{fmtDate(m.data_fim_gratuidade)}</Td>
                  <Td><StatusBadge s={m.status_assinatura} /></Td>
                  <Td className="text-center font-mono">{m.atendimentos_mes_corrente || 0}</Td>
                  <Td className="text-xs">{m.faixa_atual || "—"}</Td>
                  <Td>R$ {Number(m.valor_projetado || 0).toFixed(2)}</Td>
                  <Td><ActionBtns tipo="montadora" id={m.id} statusAss={m.status_assinatura} contratoAssinado={m.contrato_assinado} onChange={refresh} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ListaEmpresas() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/assinaturas/empresas").then(r => setItems(r.data || [])).catch(() => {});
  }, []);
  return (
    <div className="border border-border bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-left">
          <tr>
            <Th>Nome</Th><Th>CNPJ</Th><Th>Cidades</Th><Th>Cadastro</Th><Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhuma empresa cadastrada.</td></tr>
          ) : items.map(e => (
            <tr key={e.id} className="border-t border-border" data-testid={`row-emp-${e.id}`}>
              <Td><strong>{e.nome}</strong></Td>
              <Td className="text-xs font-mono">{e.cnpj}</Td>
              <Td className="text-xs">{(e.cidades_atendidas || []).slice(0, 3).join(", ") || "—"}</Td>
              <Td>{fmtDate(e.created_at)}</Td>
              <Td><StatusBadge s={e.status_assinatura} /></Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-3 text-xs text-muted-foreground border-t border-border">
        Planos da empresa (START/GROWTH/BUSINESS/CORPORATE/ENTERPRISE) serão configuráveis na Fase C.
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="p-3 label-cap text-[10px] font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
